// The photo viewer's sizing, zoom and keyboard rules (2026-10-27,
// docs/contact-view.md "Photo viewer"), pure so they are unit tested
// (ui/test/lightboxView.test.js). ImageLightbox.jsx renders them.
//
// Sizes:
//   fit    the image fitted into the viewing box: shrunk to fit a large one,
//          and a small one enlarged up to MAX_AUTO_UPSCALE so it is readable
//          but not mushy (the opening size, as before).
//   zoom   a multiplier on fit (1 = fit). 100% and 200% are the full-size
//          image's own pixels, once and twice: zoomForPercent().
// Rotation turns the image in 90 degree steps; at 90 and 270 the fit uses the
// turned width and height.

// A native-small image (a cropped phone screenshot) renders unreadably tiny
// at true pixel size, so the fit enlarges it up to this multiplier.
export const MAX_AUTO_UPSCALE = 2
export const ZOOM_MIN = 0.25
export const ZOOM_MAX = 40
export const WHEEL_STEP = 1.15
export const BUTTON_STEP = 1.25

export function clampZoom(z) {
  const n = Number(z)
  if (!Number.isFinite(n) || n <= 0) return 1
  return Math.min(Math.max(n, ZOOM_MIN), ZOOM_MAX)
}

export function isSideways(rotation) {
  return ((Number(rotation) % 360) + 360) % 180 === 90
}

// The fitted size of the <img> (its own, unturned width and height), or null
// before the image's size is known. natural: { width, height } of the image
// the fit is measured on; box: { width, height } of the viewing area.
export function fitSize(natural, box, rotation = 0) {
  if (!natural || !box || !(natural.width > 0) || !(natural.height > 0) || !(box.width > 0) || !(box.height > 0)) return null
  const sideways = isSideways(rotation)
  const w = sideways ? natural.height : natural.width
  const h = sideways ? natural.width : natural.height
  const fitScale = Math.min(box.width / w, box.height / h)
  const scale = fitScale > 1 ? Math.min(fitScale, MAX_AUTO_UPSCALE) : fitScale
  return { width: natural.width * scale, height: natural.height * scale }
}

// The zoom (a multiplier on fit) that shows the full-size image at percent
// of its own pixels. fit: fitSize()'s result; fullNatural: the full-size
// image's own width and height (the shown image's when no larger copy
// loaded).
export function zoomForPercent(percent, fit, fullNatural) {
  if (!fit || !(fit.width > 0) || !fullNatural || !(fullNatural.width > 0)) return 1
  return clampZoom((fullNatural.width * (Number(percent) / 100)) / fit.width)
}

// One wheel notch (or trackpad pinch step): up zooms in, down zooms out.
export function wheelZoom(zoom, deltaY) {
  if (!deltaY) return clampZoom(zoom)
  return clampZoom(deltaY < 0 ? zoom * WHEEL_STEP : zoom / WHEEL_STEP)
}

// A two-finger pinch: the zoom when the fingers started, scaled by how far
// apart they are now.
export function pinchZoom(startZoom, startDistance, distance) {
  if (!(startDistance > 0) || !(distance > 0)) return clampZoom(startZoom)
  return clampZoom(startZoom * (distance / startDistance))
}

export function nextRotation(rotation) {
  return ((Number(rotation) || 0) + 90) % 360
}

// The keyboard: + / - zoom, 0 fits, R rotates, Esc closes, the arrows move
// between images. null for any other key.
export function lightboxKeyAction(key) {
  switch (key) {
    case '+': case '=': return 'zoom_in'
    case '-': case '_': return 'zoom_out'
    case '0': return 'fit'
    case 'r': case 'R': return 'rotate'
    case 'Escape': return 'close'
    case 'ArrowLeft': return 'prev'
    case 'ArrowRight': return 'next'
    default: return null
  }
}

// Whether "Open original" can open the URL in a new tab: an http(s) or blob:
// URL. A data: URL (a reply-box attachment) is refused by browsers as a
// top-level page, so the button is not shown for one.
export function canOpenOriginal(url) {
  return typeof url === 'string' && /^(?:https?:|blob:)/i.test(url)
}

// What the viewer's <img> shows first (2026-10-27, Jenn): the full-size copy
// right away, never the small stored copy first. A URL with no larger copy
// (an attachment's blob: or data: URL) is shown as is.
export function initialViewerSrc(storedUrl, fullUrl) {
  return fullUrl || storedUrl || null
}

// The full-size copy failed to load: the stored copy, once, with "Showing a
// smaller copy". null when the stored copy is what failed (nothing smaller
// to fall back to, and no retry loop).
export function fallbackViewerSrc(shownSrc, storedUrl) {
  return storedUrl && shownSrc !== storedUrl ? storedUrl : null
}
